import { useState, useEffect, useCallback } from 'react';
import { Project, ProjectListParams } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { captureException } from '../utils/captureException';

interface UseLatestProjectsResult {
  projects: Project[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useLatestProjects = (params: ProjectListParams = {}): UseLatestProjectsResult => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit.toString());
      
      const queryString = queryParams.toString();
      const url = `${process.env.REACT_APP_API_URL || ''}/api/projects${queryString ? `?${queryString}` : ''}`;
      const idToken = await user?.getIdToken();
      
      if (!idToken) {
        throw new Error('No idToken found');
      }
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // console.log("data", data);
      // Transform API response to our Project interface
      const transformedProjects = Array.isArray(data) 
        ? data.map((item: any) => ({
            id: item.project_code ,
            project_code: item.project_code,
            createdAt: item.createdAt,
            status: item.status,
            createdBy: item.createdBy,
            lastModifiedBy: item.lastModifiedBy,
            lastModifiedAt: item.lastModifiedAt,
            fecha_entrega: item.fecha_entrega,
            contributors: item.contributors,
            "CONTROL CAMBIOS": item.CONTROL_CAMBIOS,
            "GARANTIA E IDIOMA": item.GARANTIA_E_IDIOMA,
            "DETALLES DE LA ORDEN": item.DETALLES_DE_LA_ORDEN,
            "DETALLES DEL CHASIS": item.DETALLES_DEL_CHASIS,
            "DETALLES EQUIPO": item.DETALLES_EQUIPO,
            status_inspeccion: item.status_inspeccion,
            status_fotos: item.status_fotos,
            status_opcionales: item.status_opcionales,
            status_mediciones: item.status_mediciones,
            status_series: item.status_series,
            warning_series: item.warning_series,
            warning_mediciones: item.warning_mediciones,
            warning_opcionales: item.warning_opcionales,
            warning_fotos: item.warning_fotos,
            warning_inspeccion: item.warning_inspeccion,
            ficha_rosroca: item.ficha_rosroca,
            chasis_rosroca: item.chasis_rosroca,
            xapa_rosroca: item.xapa_rosroca,
            fotos: item.fotos,
            cover_photo: item.cover_photo,
            cover_thumbnail_photo: item.cover_thumbnail_photo,
            gcs_pdf_path: item.gcs_pdf_path,
            pdf_name: item.pdf_name,
            gcs_report_path: item.gcs_report_path,
            gcs_image_book_path: item.gcs_image_book_path,
            gcs_homologation_book_path: item.gcs_homologation_book_path,
            gcs_series_book_path: item.gcs_series_book_path,
            revision: item.revision,
            verified_by: item.verified_by,
          }))
        : [];
      
      // // sort projects by last modified at ascending
      // transformedProjects.sort((a, b) => {
      //   return new Date(a.lastModifiedAt).getTime() - new Date(b.lastModifiedAt).getTime();
      // });
      setProjects(transformedProjects);
      
    } catch (err) {
      captureException(err, { tags: { feature: 'useLatestProjects' }, extra: { action: 'fetchProjects' } });
      console.error('Error fetching projects:', err);
      setError(err instanceof Error ? err.message : 'Error desconocido al cargar proyectos');
    } finally {
      setLoading(false);
    }
  }, [params.limit, params.start_date, params.end_date]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    projects,
    loading,
    error,
    refetch: fetchProjects,
  };
};
